import { useEffect, useRef, useState } from 'react';
import { usePrevious } from 'react-use';
import { fetchAllPages } from '../helpers/apiHelper';
import { objectToSearchParams } from '../helpers/jsHelper';

export const MODE = {
  QUERY: 'QUERY',
  PARAMS: 'PARAMS',
};

export const useApiList = (
  {
    api,
    params = {},
    mode = MODE.QUERY,
    query = '',
    defaultPerPage = 20,
    skip = false,
    dataFormatter,
    fetchAll = false,
    initialLoading = false
  },
  deps = []
) => {
  const [list, setList] = useState([]);
  const prevSkip = usePrevious(skip);
  const [loading, setLoading] = useState(initialLoading);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(defaultPerPage);
  const [hasMore, setHasMore] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [total, setTotal] = useState(0);
  const listRef = useRef([]);
  const totalRef = useRef(0);
  const fetchAllTriggered = useRef(false);
  listRef.current = list;
  totalRef.current = total;

  useEffect(() => {
    if (prevSkip && !skip) {
      if (fetchAll) {
        fetchAllTriggered.current = false;
        _fetchAll();
      } else {
        fetchList(1);
      }
    }
  }, [skip]);

  useEffect(() => {
    if (fetchAll) {
      fetchAllTriggered.current = false;
      _fetchAll();
    } else {
      fetchList(1);
    }
  }, [JSON.stringify(params), query, ...deps]);

  useEffect(() => {
    if (totalRef.current === 0) {
      setHasData(false);
    } else {
      setHasData(true);
    }
  }, [totalRef.current]);

  const callApi = async (nextPage) => {
    let res;
    if (mode === MODE.QUERY) {
      const paramsStr = objectToSearchParams(params);
      res = await api?.(`&page=${nextPage}&per_page=${perPage}&${paramsStr}&${query}`);
    } else {
      res = await api?.({ page: nextPage, per_page: perPage, ...params });
    }

    return res;
  };

  const fetchList = async (nextPage) => {
    if (skip) return;

    setLoading(true);

    const res = await callApi(nextPage);

    if (res?.results) {
      let index = 1;
      const formattedData = dataFormatter
        ? res.results.map((item) => {
            const seq =
              +item?.seq_no || parseInt(item?.order) || parseInt(item?.sequence_number) || index;
            index = seq + 1;
            return dataFormatter({ ...item, seq_no: seq });
          })
        : res.results;

      nextPage === 1 ? setList(formattedData) : setList([...list, ...formattedData]);
      setHasMore(Boolean(res.next_link));

      if (nextPage === 1) {
        setTotal(res.count);
      }
    }

    setLoading(false);
    setPage(nextPage);
  };

  const _fetchAll = async () => {
    if (fetchAllTriggered.current) return;

    fetchAllTriggered.current = true;

    setLoading(true);
    const res = await fetchAllPages(callApi);

    if (res?.results) {
      const formattedData = dataFormatter ? res.results.map(dataFormatter) : res.results;
      setList(formattedData);
      setTotal(formattedData.length);
      setHasMore(false);
    }

    setLoading(false);
  };

  const fetchNextPage = () => {
    fetchList(page + 1);
  };

  const refetch = () => {
    fetchList(1);
  };

  const refetchAllData = () => {
    fetchAllTriggered.current = false;
    _fetchAll();
  };

  const add = (items) => {
    setList([...items, ...listRef.current]);
    setTotal(totalRef.current + items.length);
  };

  return {
    list,
    loading,
    page,
    hasMore,
    hasData,
    refetch,
    refetchAllData,
    fetchNextPage,
    total,
    add,
  };
};
